// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * Global keyboard shortcuts for the application.
 *
 * Shortcuts (two-key sequences starting with "%"):
 * - "%i" - Navigate to login page (only when not authenticated)
 * - "%o" - Sign out and navigate to home (only when authenticated)
 */

import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { useGameStore } from '../store/gameStore';

const SEQUENCE_TIMEOUT_MS = 1000;

export function useGlobalKeyboardShortcuts() {
  const navigate = useNavigate();
  const { isAuthenticated, clearAuth, setShowSignedOutModal } = useAuthStore();
  const { clearSession } = useGameStore();
  const prefixPressedAt = useRef<number | null>(null);

  const handleSignOut = useCallback(async () => {
    // Leave any active session
    try {
      await api.leaveMySession();
    } catch {
      // Ignore errors - session may not exist
    }

    // Call logout endpoint
    try {
      await api.logout();
    } catch {
      // Ignore errors - still clear local auth state
    }

    // Clear local state
    clearAuth();
    clearSession();

    // Show signed out modal
    setShowSignedOutModal(true);

    // Navigate to home
    navigate('/');
  }, [clearAuth, clearSession, navigate, setShowSignedOutModal]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const now = Date.now();

      // Track "%" key press (Shift+5)
      if (e.key === '%') {
        prefixPressedAt.current = now;
        return;
      }

      // Check if "%" was pressed recently
      const prefixRecent =
        prefixPressedAt.current !== null && now - prefixPressedAt.current < SEQUENCE_TIMEOUT_MS;

      if (!prefixRecent) {
        return;
      }

      // "%i" - Go to login (only when not authenticated)
      if (e.key === 'i' && !isAuthenticated()) {
        e.preventDefault();
        prefixPressedAt.current = null;
        navigate('/login');
      }

      // "%o" - Sign out (only when authenticated)
      if (e.key === 'o' && isAuthenticated()) {
        e.preventDefault();
        prefixPressedAt.current = null;
        handleSignOut();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [navigate, isAuthenticated, handleSignOut]);
}
